import {useState} from 'react';
import {
    Box,
    Collapse,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Switch,
    Typography
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronDown, faChevronUp, faHeadphones, faUserCircle} from '@fortawesome/pro-duotone-svg-icons';
import DrawerMenuProps from "@/src/_types/drawer-menu-props";
import DrawerLink from "@/src/components/common/_partials/header/drawer-link/drawer-link";
import {useTheme} from "@mui/material/styles";
import {useAppTheme} from "@/src/hooks/useAppTheme";

const DrawerMenu = ({open, toggleDrawer, menuItems, mainJournal, mainSite, supportUrl, signInUrl}: DrawerMenuProps) => {
    const {theme, toggleTheme} = useAppTheme();
    const themeMui = useTheme();
    const [collapseOpen, setCollapseOpen] = useState<boolean[]>([]);

    const handleToggle = (index: number) => {
        const newCollapseOpen = [...collapseOpen];
        newCollapseOpen[index] = !newCollapseOpen[index];
        setCollapseOpen(newCollapseOpen);
    };

    return (
        <Drawer anchor="right" open={open} onClose={() => toggleDrawer(false)}>
            <Box sx={{width: 250}} role="presentation" onKeyDown={() => toggleDrawer(false)}>
                <List subheader={<ListSubheader
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        flexDirection: "column",
                        gap: 0.5,
                        paddingY: 0.5,
                        backgroundColor: themeMui.palette.background.default
                    }}
                >
                    <Typography component={"h1"} sx={{fontWeight: 700, fontSize: "1.25rem"}}>
                        {mainJournal.title}
                    </Typography>
                </ListSubheader>}>
                    {menuItems?.map((item, index) => (
                        <Box key={index}>
                            {!item.children ? (
                                <DrawerLink
                                    color={themeMui.palette.primary.main}
                                    link={item.link || '#'}
                                    key={index}>
                                    <ListItem>
                                        <ListItemIcon>
                                            <i className={item.icon}/>
                                        </ListItemIcon>
                                        <ListItemText primary={item.label}/>
                                    </ListItem>
                                </DrawerLink>
                            ) : (
                                <>
                                    <ListItem onClick={() => handleToggle(index)}>
                                        <ListItemIcon>
                                            <i className={item.icon}/>
                                        </ListItemIcon>
                                        <ListItemText primary={item.label}/>
                                        {collapseOpen[index] ? (
                                            <FontAwesomeIcon icon={faChevronUp}/>
                                        ) : (
                                            <FontAwesomeIcon icon={faChevronDown}/>
                                        )}
                                    </ListItem>
                                    <Collapse in={collapseOpen[index]} sx={{ml: 1}}>
                                        <List component="div" disablePadding>
                                            {item.children.map((child, childIndex) => (
                                                <DrawerLink
                                                    color={themeMui.palette.primary.main}
                                                    link={child.link || '#'}
                                                    key={childIndex}>
                                                    <ListItem>
                                                        <ListItemIcon>
                                                            <i className={child.icon}/>
                                                        </ListItemIcon>
                                                        <ListItemText primary={child.label}/>
                                                    </ListItem>
                                                </DrawerLink>
                                            ))}
                                        </List>
                                    </Collapse>
                                </>
                            )}
                        </Box>
                    ))}
                    <DrawerLink
                        color={themeMui.palette.primary.main}
                        link={supportUrl}>
                        <ListItem>
                            <ListItemIcon>
                                <FontAwesomeIcon icon={faHeadphones} size={'sm'} color={themeMui.palette.primary.main}/>
                            </ListItemIcon>
                            <ListItemText primary={'Support'}/>
                        </ListItem>
                    </DrawerLink>
                    {
                        !mainSite && (
                            <DrawerLink
                                color={themeMui.palette.primary.main}
                                link={signInUrl}>
                                <ListItem>
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faUserCircle} style={{fontSize: '1.75rem'}}/>
                                    </ListItemIcon>
                                    <ListItemText primary={'Sign In'}/>
                                </ListItem>
                            </DrawerLink>
                        )
                    }
                    <Box
                        sx={{
                            textDecoration: 'none',
                            color: themeMui.palette.primary.main,
                            fontWeight: 700,
                        }}>
                        <ListItem>
                            <ListItemText primary={'Toggle Dark Mode'}/>
                            <Switch
                                checked={theme === "dark"}
                                onChange={toggleTheme}
                                sx={{
                                    "& .MuiSwitch-thumb": {
                                        color: theme === "dark" ? themeMui.palette.primary.main : "yellow",
                                    },
                                    "& .MuiSwitch-track": {
                                        backgroundColor: theme === "dark" ? themeMui.palette.primary.light : "lightgray",
                                    },
                                }}
                            />
                        </ListItem>
                    </Box>
                </List>
            </Box>
        </Drawer>
    );
};

export default DrawerMenu;
